import React from "react";

import { DraftJsContentState } from "@reactivated";

import { EntityComponent } from "./renderer";

import styles from "./rich-text.module.scss";

interface FootnoteNumbering {
    refNumberByEntityKey: Map<string, number>;
    itemNumberByBlockKey: Map<string, number>;
    refCount: number;
    itemCount: number;
}

// Keyed by the content state object itself: each RichText looks up the very
// value its block passed down, which is unique per block in page props.
type ScopeNumbering = Map<DraftJsContentState, FootnoteNumbering>;

/**
 * Walks each content state in reading order and numbers the refs and notes
 * across all of them, so a cite point in one rich text block pairs with a note
 * in another. Each state gets its own lookup maps:
 *   - refNumberByEntityKey: FOOTNOTE_REF entity key → its 1-based index
 *   - itemNumberByBlockKey: footnote-list-item block key → its 1-based index
 * (entity and block keys are only unique within one state). These generate
 * matching #fnref-N / #fn-N anchors so a footnote's ↩ backlink lands at its
 * cite point.
 */
function computeFootnoteNumbering(
    values: DraftJsContentState[],
): ScopeNumbering {
    let refCount = 0;
    let itemCount = 0;
    const perValue = values.map((value) => {
        const refNumberByEntityKey = new Map<string, number>();
        const itemNumberByBlockKey = new Map<string, number>();
        for (const block of value.blocks) {
            if (block.type === "footnote-list-item") {
                // Refs inside a note are left unnumbered (and render nothing),
                // so a stray click with the cursor in a note can't shift every
                // body ref after it.
                itemCount += 1;
                itemNumberByBlockKey.set(block.key, itemCount);
                continue;
            }
            const ranges = [...block.entityRanges].sort(
                (a, b) => a.offset - b.offset,
            );
            for (const range of ranges) {
                const entity = value.entityMap[String(range.key)];
                if (entity.type === "FOOTNOTE_REF") {
                    refCount += 1;
                    refNumberByEntityKey.set(String(range.key), refCount);
                }
            }
        }
        return { value, refNumberByEntityKey, itemNumberByBlockKey };
    });
    return new Map(
        perValue.map(({ value, ...maps }) => [
            value,
            { ...maps, refCount, itemCount },
        ]),
    );
}

const isContentState = (node: unknown): node is DraftJsContentState =>
    typeof node === "object" &&
    node !== null &&
    "blocks" in node &&
    "entityMap" in node &&
    Array.isArray(node.blocks);

/**
 * The content of each top-level rich_text block. Rich text nested in other
 * blocks (FAQ answers, reusable content) keeps its own numbering, so a Notes
 * list there can't shift the article's numbers.
 */
const richTextBodies = (
    blocks: readonly { type: string; value: unknown }[],
): DraftJsContentState[] =>
    blocks.flatMap((block) => {
        const value = block.value;
        if (
            block.type !== "rich_text" ||
            typeof value !== "object" ||
            value === null ||
            !("content" in value)
        ) {
            return [];
        }
        return isContentState(value.content) ? [value.content] : [];
    });

const scopeContext = React.createContext<ScopeNumbering | null>(null);

/**
 * Numbers footnotes across every top-level rich_text block in a StreamField,
 * so refs in the body pair with a Notes list in a later block.
 */
export const FootnoteScope = ({
    blocks,
    children,
}: React.PropsWithChildren<{
    blocks: readonly { type: string; value: unknown }[];
}>) => {
    const numbering = React.useMemo(
        () => computeFootnoteNumbering(richTextBodies(blocks)),
        [blocks],
    );
    return (
        <scopeContext.Provider value={numbering}>
            {children}
        </scopeContext.Provider>
    );
};

const footnoteContext = React.createContext<FootnoteNumbering>({
    refNumberByEntityKey: new Map(),
    itemNumberByBlockKey: new Map(),
    refCount: 0,
    itemCount: 0,
});

/**
 * Supplies one RichText value's numbering: its slice of the enclosing
 * FootnoteScope, or numbering of just this value when it isn't part of one.
 */
export const FootnoteNumberingProvider = ({
    value,
    children,
}: React.PropsWithChildren<{ value: DraftJsContentState }>) => {
    const scope = React.useContext(scopeContext);
    const numbering = React.useMemo(
        () =>
            scope?.get(value) ?? computeFootnoteNumbering([value]).get(value)!,
        [scope, value],
    );
    return (
        <footnoteContext.Provider value={numbering}>
            {children}
        </footnoteContext.Provider>
    );
};

export const FootnoteRef = (({ entity }) => {
    const { refNumberByEntityKey, itemCount } =
        React.useContext(footnoteContext);
    const key = entity?.entityRange.key;
    const n =
        typeof key === "string" ? refNumberByEntityKey.get(key) : undefined;
    if (!n) return null;
    // A ref with no matching note keeps its number but gets no link, so it
    // never points at a missing #fn-N.
    if (n > itemCount) {
        return <sup className={styles.footnoteRef}>{n}</sup>;
    }
    return (
        <sup className={styles.footnoteRef}>
            <a
                href={`#fn-${n}`}
                id={`fnref-${n}`}
                aria-label={`Jump to footnote ${n}`}
            >
                {n}
            </a>
        </sup>
    );
}) satisfies EntityComponent;

export function FootnoteItem({
    block,
    children,
}: React.PropsWithChildren<{ block: { key: string } }>) {
    const { itemNumberByBlockKey, refCount } =
        React.useContext(footnoteContext);
    const n = itemNumberByBlockKey.get(block.key);
    const hasBacklink = n !== undefined && n <= refCount;
    const isFootnoted = n !== undefined && refCount > 0;
    // `value` pins the displayed number to n, so a Notes list split into
    // several <ol>s doesn't restart at 1 and drift from its fn-N anchor.
    // Bare Notes lists (no refs anywhere) keep their original markup.
    return (
        <li
            value={isFootnoted ? n : undefined}
            id={isFootnoted ? `fn-${n}` : undefined}
        >
            {children}
            {hasBacklink ? (
                <>
                    {" "}
                    <a
                        href={`#fnref-${n}`}
                        className={styles.footnoteBacklink}
                        aria-label={`Back to reference ${n}`}
                    >
                        {"↩︎"}
                    </a>
                </>
            ) : null}
        </li>
    );
}
